import ClearIcon from '@mui/icons-material/Clear';
import { MenuItem } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';
import {
  Controller,
  UseFieldArrayReturn,
  useFormContext,
  useWatch,
} from 'react-hook-form';

import { ProjectData } from './new-project-wizard-dialog';

import Arranger from '~/client/components/base/arranger';
import TextField from '~/client/components/base/text-field';
import { useOrganizationMembersPaginatedQuery } from '~/graphql/generated/client';
import Spacer, { spc } from '~c/components/base/spacer';

interface Props {
  className?: string;
  index: number;
  readOnly?: boolean;
  organizationId?: string;
  removeMember?: UseFieldArrayReturn<ProjectData, 'members'>['remove'];
}

const MIN_SUGGEST_CHARS = 3;
const SUGGEST_DEBOUNCE_MS = 300;

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const roles = [
  { value: 'selectrole', label: 'Select a role' },
  { value: 'OWNER', label: 'Owner' },
  { value: 'CONTRIBUTOR', label: 'Contributor' },
  { value: 'REVIEWER', label: 'Reviewer' },
  { value: 'APPROVER', label: 'Approver' },
  { value: 'VIEWER', label: 'Viewer' },
  { value: 'AUDITOR', label: 'Auditor' },
];

const InviteMemberField: React.FC<Props> = ({
  className,
  readOnly,
  index,
  removeMember,
  organizationId,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<ProjectData>();

  const emailInput = useWatch({
    control,
    name: `members.${index}.email`,
  }) as string | undefined;
  const trimmedInput = (emailInput ?? '').trim();

  const [debouncedSearch, setDebouncedSearch] = React.useState('');
  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      if (trimmedInput.length >= MIN_SUGGEST_CHARS) {
        setDebouncedSearch(trimmedInput);
      } else {
        setDebouncedSearch('');
      }
    }, SUGGEST_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [trimmedInput]);

  const canSuggest =
    Boolean(organizationId) && debouncedSearch.length >= MIN_SUGGEST_CHARS;

  const { data: orgMembersData, loading: suggestionsLoading } =
    useOrganizationMembersPaginatedQuery({
      variables: {
        organizationId: organizationId ?? '',
        status: null,
        searchQuery: debouncedSearch,
        page: 1,
        rowsPerPage: 30,
      },
      skip: !canSuggest,
      fetchPolicy: 'network-only',
    });

  const emailOptions = React.useMemo(() => {
    if (trimmedInput.length < MIN_SUGGEST_CHARS) {
      return [];
    }
    if (debouncedSearch.length < MIN_SUGGEST_CHARS) {
      return [];
    }
    return (
      orgMembersData?.organizationMembersPaginated.members
        .map((m) => m.user?.email)
        .filter((e): e is string => !!e) ?? []
    );
  }, [orgMembersData, debouncedSearch, trimmedInput]);

  return (
    <Arranger className={className}>
      <Controller
        name={`members.${index}.email`}
        control={control}
        defaultValue=""
        rules={{
          required: 'Email is required',
          validate: (value) => {
            if (!value) {
              return 'Email is required';
            }
            if (!isValidEmail(value)) {
              return 'Please enter a valid email address';
            }
            return true;
          },
        }}
        render={({ field }) => (
          <Autocomplete
            freeSolo
            disableClearable
            options={emailOptions}
            filterOptions={(opts) => opts}
            inputValue={field.value}
            onInputChange={(_, newValue) => field.onChange(newValue)}
            onChange={(_, newValue) =>
              field.onChange(typeof newValue === 'string' ? newValue : '')
            }
            disabled={readOnly}
            fullWidth
            loading={
              suggestionsLoading && trimmedInput.length >= MIN_SUGGEST_CHARS
            }
            openOnFocus={false}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={field.ref}
                required
                label="Add their email"
                error={errors.members?.[index]?.email != null}
                helperText={errors.members?.[index]?.email?.message}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: removeMember ? (
                    <ClearIcon
                      sx={{ cursor: 'pointer' }}
                      onClick={() => removeMember(index)}
                    />
                  ) : (
                    params.InputProps.endAdornment
                  ),
                }}
              />
            )}
          />
        )}
      />
      <Spacer pr={spc.between_horizontal_s} />
      <Controller
        name={`members.${index}.role`}
        control={control}
        rules={{
          required: 'Role is required',
          validate: (value) => value !== 'selectrole' || 'Role is required',
        }}
        render={({ field }) => (
          <TextField
            {...field}
            label="Role"
            required
            select
            defaultValue="selectrole"
            error={errors.members?.[index]?.role != null}
            helperText={errors.members?.[index]?.role?.message}
            InputProps={{ readOnly }}
            sx={{ minWidth: 200 }}
          >
            {roles.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </Arranger>
  );
};

export default muiStyled(InviteMemberField)(({ theme }) => css``);
